import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Switch,
  Spinner,
} from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import AskSalahSubscriptionCard, { AskSalahQuotation } from '../AskSalahSubscriptionCard/AskSalahSubscriptionCard';
import { ASK_SALAH_PLANS } from 'src/types/askSalah';
import type { AskSalahBillingCycle } from 'src/types/askSalah';
import {
  useAskSalahSubscription,
  useAskSalahTrialEligibility,
  useAskSalahPaidPlanEligibility,
} from 'src/hooks/askSalah';
import {
  fetchAskSalahQuotations,
  mapCorePlanToAskSalahQuotation,
} from 'src/api/askSalah';
import useLocale from 'src/providers/useLocale';

interface AskSalahPlanSelectorProps {
  handleOpening: (quotation: AskSalahQuotation) => void;
  subscribeAskSalah: (subData: {}) => void;
  processing: boolean;
}

const AskSalahPlanSelector: React.FC<AskSalahPlanSelectorProps> = ({
  handleOpening,
  subscribeAskSalah,
  processing,
}) => {
  const [locale] = useLocale();
  const [billingCycle, setBillingCycle] = useState<AskSalahBillingCycle>('monthly');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [baseQuotations, setBaseQuotations] = useState<AskSalahQuotation[]>([]);
  const [selectedMethods, setSelectedMethods] = useState<Record<string, string>>({});

  const { planType: currentPlanType, isActive: hasActiveAskSalahSubscription } =
    useAskSalahSubscription();
  const { isEligible: trialEligible, message: trialMessage } = useAskSalahTrialEligibility();
  const { eligible: paidEligible, message: paidMessage } = useAskSalahPaidPlanEligibility();

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    fetchAskSalahQuotations(locale)
      .then((plans) => {
        if (cancelled || !Array.isArray(plans)) return;
        setBaseQuotations(
          plans.map((plan) => {
            const mapped = mapCorePlanToAskSalahQuotation(plan);
            const planType = mapped.planType as keyof typeof ASK_SALAH_PLANS;
            return {
              ...mapped,
              features: ASK_SALAH_PLANS[planType]?.features ?? [],
              isPopular: planType === 'premium',
              selected: undefined,
            };
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setBaseQuotations([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const askSalahQuotations = useMemo(() => {
    const trialPlans = baseQuotations.filter((q) => q.planType === 'trial');
    const paidPlans = baseQuotations.filter(
      (q) =>
        q.planType !== 'trial' &&
        (q.billingCycle === billingCycle || !q.billingCycle),
    );
    const combined = [...trialPlans, ...paidPlans];
    return combined.map((q) => ({
      ...q,
      selected: selectedMethods[q.id ?? ''] ?? q.selected,
    }));
  }, [baseQuotations, billingCycle, selectedMethods]);

  const handleToggleBilling = () => {
    setBillingCycle((prev) => (prev === 'monthly' ? 'yearly' : 'monthly'));
  };

  const selectMethod = (quotationId: string, method: string) => {
    setSelectedMethods((prev) => ({ ...prev, [quotationId]: method }));
  };

  const updateProveUrl = (quotationId: string, proveUrl: string) => {
    setBaseQuotations((prev) =>
      prev.map((q) => (q.id === quotationId ? { ...q, proveUrl } : q)),
    );
  };

  if (catalogLoading) {
    return (
      <Box py={12} textAlign="center">
        <Spinner color="brand.900" />
      </Box>
    );
  }

  if (!askSalahQuotations.length) {
    return (
      <Box py={6} textAlign="center">
        <Text color="gray.500">
          <FormattedMessage id="wentWrong" defaultMessage="Something went wrong" />
        </Text>
      </Box>
    );
  }

  return (
    <Box py={6}>
      <VStack spacing={8} align="stretch">
        <VStack spacing={2}>
          <Text fontSize="3xl" fontWeight="bold" textAlign="center" color="gray.700">
            <FormattedMessage
              id="askSalah.choosePlan"
              defaultMessage="Choose Your Ask Salah Plan"
            />
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="center">
            <FormattedMessage
              id="askSalah.choosePlan.subtitle"
              defaultMessage="Select the plan that best fits your needs"
            />
          </Text>
        </VStack>

        <VStack spacing={2}>
          <HStack justify="center" spacing={4}>
            <Text
              fontSize="lg"
              fontWeight={billingCycle === 'monthly' ? 'bold' : 'normal'}
              color={billingCycle === 'monthly' ? 'brand.900' : 'gray.500'}
            >
              <FormattedMessage id="askSalah.monthly" defaultMessage="Monthly" />
            </Text>
            <Switch
              size="lg"
              colorScheme="blue"
              isChecked={billingCycle === 'yearly'}
              onChange={handleToggleBilling}
            />
            <Text
              fontSize="lg"
              fontWeight={billingCycle === 'yearly' ? 'bold' : 'normal'}
              color={billingCycle === 'yearly' ? 'brand.900' : 'gray.500'}
            >
              <FormattedMessage id="askSalah.yearly" defaultMessage="Yearly" />
            </Text>
            {billingCycle === 'yearly' && (
              <Text fontSize="sm" color="green.500" fontWeight="semibold">
                (<FormattedMessage id="askSalah.saveWithYearly" defaultMessage="Save 17%" />)
              </Text>
            )}
          </HStack>
        </VStack>

        <HStack
          display="flex"
          flexDir={{ base: 'column', lg: 'row' }}
          alignItems={{ base: 'center' }}
          justifyContent="center"
        >
          {askSalahQuotations.map((quotation) => (
            <AskSalahSubscriptionCard
              key={quotation.id}
              quotation={quotation}
              selectMethod={selectMethod}
              handleOpening={handleOpening}
              subscribeAskSalah={subscribeAskSalah}
              processing={processing}
              isCurrentPlan={
                hasActiveAskSalahSubscription &&
                currentPlanType === quotation.planType
              }
              disabled={
                quotation.planType === 'trial' ? !trialEligible : !paidEligible
              }
              disabledReason={
                quotation.planType === 'trial'
                  ? !trialEligible
                    ? trialMessage
                    : undefined
                  : !paidEligible
                    ? paidMessage
                    : undefined
              }
              updateProveUrl={updateProveUrl}
            />
          ))}
        </HStack>
      </VStack>
    </Box>
  );
};

export default AskSalahPlanSelector;
